import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { HitApi } from '../../Api/ApiHit'
import {
    searchConfigProject,
    searchPaymentMethod,
    addPaymentMethod,
    updatePaymentMethod,
    deletePaymentMethod
} from '../../constant/Constant'
import { getSelectedUnit } from '../../storage/Storage'
import { setApiJson } from '../../redux/Actions/ApiAction'
import FileuploadComp from '../../components/SingleFileUpload/FileuploadComp'
import toast from 'react-hot-toast'
import {
    CreditCard, Plus, Trash2, X, Pencil, FolderOpen,
    ChevronDown, Loader2, Link, Eye, EyeOff
} from 'lucide-react'

const PaymentMethod = () => {
    const dispatch = useDispatch()
    const ApiReducer = useSelector(state => state.ApiReducer)

    // Project state
    const [projects, setProjects] = useState([])
    const [selectedProject, setSelectedProject] = useState(null)
    const [projectsLoading, setProjectsLoading] = useState(false)
    const [showProjectDropdown, setShowProjectDropdown] = useState(false)

    // Payment methods state
    const [paymentMethods, setPaymentMethods] = useState([])
    const [methodsLoading, setMethodsLoading] = useState(false)

    // Add modal state
    const [showAddModal, setShowAddModal] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [formData, setFormData] = useState({
        name: '',
        upiId: '',
        payeeName: '',
        defaultAmount: '',
        currency: 'INR',
        transactionNote: '',
        status: 'active'
    })

    // Edit modal state
    const [showEditModal, setShowEditModal] = useState(false)
    const [editingMethod, setEditingMethod] = useState(null)
    const [editFormData, setEditFormData] = useState({
        name: '',
        upiId: '',
        payeeName: '',
        defaultAmount: '',
        currency: 'INR',
        transactionNote: '',
        status: 'active'
    })

    const unitId = getSelectedUnit()?._id || getSelectedUnit()?.[0]

    // Fetch projects on mount
    useEffect(() => {
        fetchProjects()
    }, [unitId])

    // Fetch payment methods when project changes
    useEffect(() => {
        if (selectedProject) {
            fetchPaymentMethods()
        } else {
            setPaymentMethods([])
        }
    }, [selectedProject])

    // Close project dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showProjectDropdown && !e.target.closest('.project-dropdown')) {
                setShowProjectDropdown(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [showProjectDropdown])

    const generateUpiLink = (upiId, payeeName, amount, currency, transactionNote) => {
        if (!upiId) return null
        const params = new URLSearchParams()
        params.set('pa', upiId)
        if (payeeName) params.set('pn', payeeName)
        if (amount) params.set('am', amount)
        if (currency) params.set('cu', currency)
        if (transactionNote) params.set('tn', transactionNote)
        return `upi://pay?${params.toString()}`
    }

    const fetchProjects = async () => {
        if (!unitId) return
        setProjectsLoading(true)
        try {
            const json = {
                page: 1,
                limit: 100,
                search: { unitIds: unitId, isActive: true },
                sort: { createdAt: -1 }
            }
            const res = await HitApi(json, searchConfigProject)
            const projectList = res?.data?.docs || []
            setProjects(projectList)
            if (projectList.length > 0 && !selectedProject) {
                setSelectedProject(projectList[0])
            } else if (projectList.length === 0) {
                setSelectedProject(null)
            }
        } catch (error) {
            setProjects([])
            setSelectedProject(null)
        }
        setProjectsLoading(false)
    }

    const fetchPaymentMethods = async () => {
        if (!selectedProject?._id) return
        setMethodsLoading(true)
        try {
            const json = {
                page: 1,
                limit: 100,
                search: { projectId: selectedProject._id }
            }
            const res = await HitApi(json, searchPaymentMethod)
            if (res?.statusCode === 200 && res?.data?.docs) {
                setPaymentMethods(res.data.docs)
            } else {
                setPaymentMethods([])
            }
        } catch (error) {
            console.error('Error fetching payment methods:', error)
            setPaymentMethods([])
        }
        setMethodsLoading(false)
    }

    const resetAddForm = () => {
        setFormData({
            name: '',
            upiId: '',
            payeeName: '',
            defaultAmount: '',
            currency: 'INR',
            transactionNote: '',
            status: 'active'
        })
        dispatch(setApiJson({ image: '' }))
    }

    const handleAddPaymentMethod = async () => {
        if (!formData.name.trim()) {
            toast.error('Please enter payment method name')
            return
        }
        if (!selectedProject?._id) {
            toast.error('Please select a project first')
            return
        }

        setIsSubmitting(true)
        try {
            const upiLink = generateUpiLink(
                formData.upiId.trim(),
                formData.payeeName.trim(),
                formData.defaultAmount,
                formData.currency,
                formData.transactionNote.trim()
            )

            const image = ApiReducer?.apiJson?.image || ''

            const payload = {
                projectId: selectedProject._id,
                name: formData.name.trim(),
                upiId: formData.upiId.trim() || null,
                payeeName: formData.payeeName.trim() || null,
                defaultAmount: formData.defaultAmount ? parseFloat(formData.defaultAmount) : null,
                currency: formData.currency || 'INR',
                transactionNote: formData.transactionNote.trim() || null,
                upiLink: upiLink,
                image: image || null,
                status: 'active'
            }

            const res = await HitApi(payload, addPaymentMethod)

            if (res?.statusCode === 200 || res?.statusCode === 201) {
                toast.success('Payment method added successfully')
                resetAddForm()
                setShowAddModal(false)
                fetchPaymentMethods()
            } else {
                toast.error(res?.message || 'Failed to add payment method')
            }
        } catch (error) {
            console.error('Error adding payment method:', error)
            toast.error('Failed to add payment method')
        }
        setIsSubmitting(false)
    }

    const openEditModal = (method) => {
        setEditingMethod(method)
        setEditFormData({
            name: method.name || '',
            upiId: method.upiId || '',
            payeeName: method.payeeName || '',
            defaultAmount: method.defaultAmount ? String(method.defaultAmount) : '',
            currency: method.currency || 'INR',
            transactionNote: method.transactionNote || '',
            status: method.status || 'active'
        })
        dispatch(setApiJson({ image: method.image || '' }))
        setShowEditModal(true)
    }

    const handleUpdatePaymentMethod = async () => {
        if (!editFormData.name.trim()) {
            toast.error('Please enter payment method name')
            return
        }

        setIsSubmitting(true)
        try {
            const upiLink = generateUpiLink(
                editFormData.upiId.trim(),
                editFormData.payeeName.trim(),
                editFormData.defaultAmount,
                editFormData.currency,
                editFormData.transactionNote.trim()
            )

            const image = ApiReducer?.apiJson?.image || ''

            const payload = {
                _id: editingMethod._id,
                name: editFormData.name.trim(),
                upiId: editFormData.upiId.trim() || null,
                payeeName: editFormData.payeeName.trim() || null,
                defaultAmount: editFormData.defaultAmount ? parseFloat(editFormData.defaultAmount) : null,
                currency: editFormData.currency || 'INR',
                transactionNote: editFormData.transactionNote.trim() || null,
                upiLink: upiLink,
                image: image || null,
                status: editFormData.status
            }

            const res = await HitApi(payload, updatePaymentMethod)

            if (res?.statusCode === 200 || res?.statusCode === 201) {
                toast.success('Payment method updated successfully')
                setShowEditModal(false)
                setEditingMethod(null)
                dispatch(setApiJson({ image: '' }))
                fetchPaymentMethods()
            } else {
                toast.error(res?.message || 'Failed to update payment method')
            }
        } catch (error) {
            console.error('Error updating payment method:', error)
            toast.error('Failed to update payment method')
        }
        setIsSubmitting(false)
    }

    const handleDeletePaymentMethod = async (method) => {
        if (!window.confirm(`Are you sure you want to delete "${method.name}"?`)) return

        try {
            const res = await HitApi({ _id: method._id }, deletePaymentMethod)
            if (res?.statusCode === 200 || res?.statusCode === 201) {
                toast.success('Payment method deleted successfully')
                fetchPaymentMethods()
            } else {
                toast.error(res?.message || 'Failed to delete payment method')
            }
        } catch (error) {
            console.error('Error deleting payment method:', error)
            toast.error('Failed to delete payment method')
        }
    }

    const handleToggleStatus = async (method) => {
        try {
            const newStatus = method.status === 'active' ? 'inactive' : 'active'
            const res = await HitApi({ _id: method._id, status: newStatus }, updatePaymentMethod)
            if (res?.statusCode === 200) {
                toast.success(`Payment method ${newStatus === 'active' ? 'activated' : 'deactivated'}`)
                fetchPaymentMethods()
            }
        } catch (error) {
            console.error('Error toggling status:', error)
        }
    }

    // Form field renderer
    const renderFormFields = (data, setData, isEdit = false) => (
        <div className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                    type="text"
                    placeholder="e.g. UPI, Cash, Card"
                    value={data.name}
                    onChange={(e) => setData({ ...data, name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">UPI ID</label>
                <input
                    type="text"
                    placeholder="e.g. yourname@okhdfcbank"
                    value={data.upiId}
                    onChange={(e) => setData({ ...data, upiId: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payee Name</label>
                <input
                    type="text"
                    placeholder="e.g. Elevate Lifestyle"
                    value={data.payeeName}
                    onChange={(e) => setData({ ...data, payeeName: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Default Amount</label>
                    <input
                        type="number"
                        placeholder="e.g. 500"
                        value={data.defaultAmount}
                        onChange={(e) => setData({ ...data, defaultAmount: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                    <input
                        type="text"
                        placeholder="INR"
                        value={data.currency}
                        onChange={(e) => setData({ ...data, currency: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Note</label>
                <input
                    type="text"
                    placeholder="e.g. Salon Payment"
                    value={data.transactionNote}
                    onChange={(e) => setData({ ...data, transactionNote: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
            </div>

            <div>
                <FileuploadComp
                    title="QR Image"
                    name="image"
                    allowed={['img']}
                />
            </div>

            {isEdit && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                        value={data.status}
                        onChange={(e) => setData({ ...data, status: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                    >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </div>
            )}

            {data.upiId && (
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-xs font-medium text-purple-700 mb-1">UPI Link Preview:</p>
                    <p className="text-xs text-purple-600 break-all">
                        {generateUpiLink(data.upiId, data.payeeName, data.defaultAmount, data.currency, data.transactionNote)}
                    </p>
                </div>
            )}
        </div>
    )

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">

                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <CreditCard className="w-7 h-7 text-gray-700" />
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Payment Methods</h1>
                            <p className="text-sm text-gray-500">Manage payment methods for your projects</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Project Selector */}
                        <div className="relative project-dropdown">
                            <button
                                onClick={() => setShowProjectDropdown(!showProjectDropdown)}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 min-w-[200px]"
                            >
                                <FolderOpen className="w-4 h-4 text-gray-500" />
                                <span className="flex-1 text-left text-sm font-medium text-gray-700 truncate">
                                    {projectsLoading ? 'Loading...' : (selectedProject?.name || 'Select Project')}
                                </span>
                                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showProjectDropdown ? 'rotate-180' : ''}`} />
                            </button>

                            {showProjectDropdown && (
                                <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                                    {projects.length === 0 ? (
                                        <div className="px-4 py-3 text-sm text-gray-500 text-center">No projects found</div>
                                    ) : (
                                        projects.map((project) => (
                                            <button
                                                key={project._id}
                                                onClick={() => {
                                                    setSelectedProject(project)
                                                    setShowProjectDropdown(false)
                                                }}
                                                className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 flex items-center gap-2 ${selectedProject?._id === project._id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                                            >
                                                <FolderOpen className="w-4 h-4" />
                                                {project.name}
                                            </button>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Add Button */}
                        {selectedProject && (
                            <button
                                onClick={() => {
                                    resetAddForm()
                                    setShowAddModal(true)
                                }}
                                className="flex items-center gap-2 bg-black text-white px-4 py-2.5 rounded-lg hover:bg-gray-800 transition-colors font-medium"
                            >
                                <Plus className="w-4 h-4" />
                                Add Payment Method
                            </button>
                        )}
                    </div>
                </div>

                {/* No Project Selected */}
                {!selectedProject && !projectsLoading && (
                    <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                        <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                            {projects.length === 0 ? 'No Projects Found' : 'Select a Project'}
                        </h3>
                        <p className="text-gray-500">
                            {projects.length === 0
                                ? 'Create a project first from the Projects page to manage payment methods.'
                                : 'Select a project from the dropdown above to manage its payment methods.'}
                        </p>
                    </div>
                )}

                {/* Loading Projects */}
                {projectsLoading && (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-10 h-10 text-gray-400 animate-spin" />
                    </div>
                )}

                {/* Payment Methods Content */}
                {selectedProject && (
                    <>
                        {/* Stats Bar */}
                        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
                            <div className="flex items-center gap-6">
                                <div>
                                    <p className="text-sm text-gray-500 font-medium">Project</p>
                                    <p className="text-lg font-bold text-gray-900">{selectedProject.name}</p>
                                </div>
                                <div className="h-10 w-px bg-gray-200"></div>
                                <div>
                                    <p className="text-sm text-gray-500 font-medium">Total Methods</p>
                                    <p className="text-lg font-bold text-gray-900">{paymentMethods.length}</p>
                                </div>
                                <div className="h-10 w-px bg-gray-200"></div>
                                <div>
                                    <p className="text-sm text-gray-500 font-medium">Active</p>
                                    <p className="text-lg font-bold text-green-600">
                                        {paymentMethods.filter(m => m.status === 'active').length}
                                    </p>
                                </div>
                                <div className="h-10 w-px bg-gray-200"></div>
                                <div>
                                    <p className="text-sm text-gray-500 font-medium">Inactive</p>
                                    <p className="text-lg font-bold text-gray-400">
                                        {paymentMethods.filter(m => m.status === 'inactive').length}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Loading Payment Methods */}
                        {methodsLoading && (
                            <div className="flex items-center justify-center py-20">
                                <Loader2 className="w-10 h-10 text-gray-400 animate-spin" />
                            </div>
                        )}

                        {/* Empty State */}
                        {!methodsLoading && paymentMethods.length === 0 && (
                            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                                <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <h3 className="text-xl font-bold text-gray-900 mb-2">No Payment Methods</h3>
                                <p className="text-gray-500 mb-6">Add payment methods for this project.</p>
                                <button
                                    onClick={() => {
                                        resetAddForm()
                                        setShowAddModal(true)
                                    }}
                                    className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors font-medium"
                                >
                                    <Plus className="w-5 h-5" />
                                    Add Payment Method
                                </button>
                            </div>
                        )}

                        {/* Payment Methods Grid */}
                        {!methodsLoading && paymentMethods.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {paymentMethods.map((method) => (
                                    <div
                                        key={method._id}
                                        className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                                    >
                                        {/* Card Header */}
                                        <div className="p-5 border-b border-gray-100">
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className={`p-2.5 rounded-lg ${method.status === 'active' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                                                        <CreditCard className={`w-6 h-6 ${method.status === 'active' ? 'text-blue-600' : 'text-gray-400'}`} />
                                                    </div>
                                                    <div>
                                                        <h3 className="text-lg font-bold text-gray-900">{method.name}</h3>
                                                        <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${method.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                                            {method.status}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        onClick={() => handleToggleStatus(method)}
                                                        className={`p-1.5 rounded-lg transition-colors ${method.status === 'active' ? 'hover:bg-green-50' : 'hover:bg-gray-100'}`}
                                                        title={method.status === 'active' ? 'Deactivate' : 'Activate'}
                                                    >
                                                        {method.status === 'active' ? (
                                                            <Eye className="w-4 h-4 text-green-600" />
                                                        ) : (
                                                            <EyeOff className="w-4 h-4 text-gray-400" />
                                                        )}
                                                    </button>
                                                    <button
                                                        onClick={() => openEditModal(method)}
                                                        className="p-1.5 rounded-lg hover:bg-blue-50 transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Pencil className="w-4 h-4 text-blue-600" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeletePaymentMethod(method)}
                                                        className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4 text-red-500" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* QR Image */}
                                        {method.image && (
                                            <div className="p-4 bg-gray-50 flex justify-center">
                                                <img
                                                    src={method.image}
                                                    alt={method.name}
                                                    className="w-32 h-32 object-contain rounded-lg border border-gray-200 bg-white p-2"
                                                />
                                            </div>
                                        )}

                                        {/* Card Body */}
                                        <div className="p-5 space-y-2.5">
                                            {method.upiId && (
                                                <div className="flex items-center gap-2">
                                                    <Link className="w-4 h-4 text-gray-400 shrink-0" />
                                                    <div className="min-w-0">
                                                        <p className="text-xs text-gray-500">UPI ID</p>
                                                        <p className="text-sm font-medium text-gray-900 truncate">{method.upiId}</p>
                                                    </div>
                                                </div>
                                            )}
                                            {method.payeeName && (
                                                <div className="flex items-center gap-2">
                                                    <CreditCard className="w-4 h-4 text-gray-400 shrink-0" />
                                                    <div className="min-w-0">
                                                        <p className="text-xs text-gray-500">Payee</p>
                                                        <p className="text-sm font-medium text-gray-900 truncate">{method.payeeName}</p>
                                                    </div>
                                                </div>
                                            )}
                                            {method.defaultAmount && (
                                                <div className="flex items-center gap-2">
                                                    <span className="w-4 h-4 text-gray-400 shrink-0 text-xs font-bold text-center">Rs</span>
                                                    <div className="min-w-0">
                                                        <p className="text-xs text-gray-500">Default Amount</p>
                                                        <p className="text-sm font-medium text-gray-900">{method.currency} {method.defaultAmount}</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Card Footer */}
                                        <div className="px-5 py-3 bg-gray-50 border-t border-gray-100">
                                            <p className="text-xs text-gray-400">
                                                Created: {method.createdAt ? new Date(method.createdAt).toLocaleDateString() : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Add Payment Method Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-5 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900">Add Payment Method</h3>
                            <button onClick={() => setShowAddModal(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        <div className="p-5">
                            {renderFormFields(formData, setFormData)}
                        </div>

                        <div className="flex gap-3 p-5 border-t border-gray-100">
                            <button
                                onClick={() => setShowAddModal(false)}
                                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddPaymentMethod}
                                disabled={isSubmitting}
                                className="flex-1 px-4 py-2.5 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding...</> : 'Add'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Payment Method Modal */}
            {showEditModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-5 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900">Edit Payment Method</h3>
                            <button
                                onClick={() => {
                                    setShowEditModal(false)
                                    setEditingMethod(null)
                                    dispatch(setApiJson({ image: '' }))
                                }}
                                className="p-1 hover:bg-gray-100 rounded-lg"
                            >
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        <div className="p-5">
                            {renderFormFields(editFormData, setEditFormData, true)}
                        </div>

                        <div className="flex gap-3 p-5 border-t border-gray-100">
                            <button
                                onClick={() => {
                                    setShowEditModal(false)
                                    setEditingMethod(null)
                                    dispatch(setApiJson({ image: '' }))
                                }}
                                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdatePaymentMethod}
                                disabled={isSubmitting}
                                className="flex-1 px-4 py-2.5 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Updating...</> : 'Update'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default PaymentMethod
